///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { BehaviorSubject, first, firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { AccountService } from './account.service';
import { UserDTO } from '../../generated/shared-types.model';
import { GtthNotificationsService } from '../notifications/notifications.service';
import { GtthProjectStateService } from '../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthEventManager } from '../../services/event-manager.service';
import { AutoLogoutWorkerService, MessageType } from './auto-logout-worker.service';
import { UserActivityService } from '../auto-logout-modal/user-activity.service';
import { AcceptEulaDialogComponent } from '../../global-dialogs/eula/accept/accept-eula-dialog.component';
import { EmailVerificationDialogComponent } from '../../email-verification/email-verification-dialog.component';
import { EMAIL_VERIFICATION_CONSTANTS } from '../../email-verification/email-verification.constants';

@Injectable({ providedIn: 'root' })
export class Principal {
    private userIdentity: UserDTO;
    private authenticated = false;
    private readonly authenticationState: BehaviorSubject<UserDTO> = new BehaviorSubject<UserDTO>(null);

    private authorizationTimer = undefined;
    private readonly timeInterval = 30000;

    private token: string | null = null;

    constructor(
        private readonly ngZone: NgZone,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly account: AccountService,
        private readonly dialogService: MatDialog,
        private readonly notificationsService: GtthNotificationsService,
        private readonly projectStateService: GtthProjectStateService,
        private readonly eventManager: GtthEventManager,
        private readonly userActivityService: UserActivityService,
        private readonly autoLogoutWorkerService: AutoLogoutWorkerService
    ) {
        this.autoLogoutWorkerService.addMessageListener((event: MessageEvent): void => {
            switch (event.data.type) {
                case MessageType.LOGIN:
                    this.identity();
                    break;
                case MessageType.LOGOUT:
                    this.identity(true);
                    break;
                case MessageType.AUTO_LOGOUT:
                    this.forceLogout(true);
                    break;
                case MessageType.COUNTDOWN:
                    this.userActivityService.setCountdownCounter(event.data.counter);
                    break;
                default:
                    this.userActivityService.setCountdownCounter(null);
            }
        });
    }

    login(username: string, password: string): Observable<any> {
        const data = `j_username=${encodeURIComponent(username)}&j_password=${encodeURIComponent(password)}`;
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

        return this.http.post('api/authentication', data, { headers }).pipe(
            tap(() => {
                this.userActivityService.startTrackUserActivity();
                this.autoLogoutWorkerService.postMessage({ type: MessageType.LOGIN });
            })
        );
    }

    forceLogout(isAutoLogout = false) {
        this.dialogService.closeAll();

        this.notificationsService.showNotifications = false;

        if (this.projectStateService.getProjectId()) {
            const projectClose = this.eventManager.getEventSubscription('projectClosed').subscribe(() => {
                if (projectClose) {
                    projectClose.unsubscribe();
                }
                this.logoutRequest(isAutoLogout);
            });
        } else {
            console.log('run logout without project');
            return this.logoutRequest(isAutoLogout);
        }
    }

    authenticate(identity: UserDTO | null): void {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        if (this.authenticationState.value !== identity) {
            this.authenticationState.next(this.userIdentity);
        }
    }

    hasAnyAuthority(authorities: string[]): Promise<boolean> {
        if (!this.authenticated || !this.userIdentity?.authorities || this.userIdentity.authorities.length === 0) {
            return Promise.resolve(false);
        }

        for (const authority of authorities) {
            if (this.userIdentity.authorities.indexOf(authority) !== -1) {
                return Promise.resolve(true);
            }
        }

        return Promise.resolve(false);
    }

    hasAuthority(authority: string): Promise<boolean> {
        if (!this.authenticated) {
            return Promise.resolve(false);
        }

        return this.identity().then(
            (user: UserDTO) => {
                return Promise.resolve(user.authorities && user.authorities.indexOf(authority) !== -1);
            },
            () => {
                return Promise.resolve(false);
            }
        );
    }

    identity(force = false): Promise<UserDTO> {
        if (force) {
            this.userIdentity = undefined;
            /*
             * need to stop tracking user activity synchronously
             * to avoid timers reset before the account request is finished
             */
            this.userActivityService.stopTrackUserActivity();
        }

        /*
         * check and see if we have retrieved the userIdentity data from the server.
         * if we have, reuse it by immediately resolving
         */
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }

        /*
         * if no user session exists, we need to store the token from the URL and use it after log in
         * for use case when the default browser is not the same as the one used to trigger verification email sending
         */
        if (!this.token) {
            this.token = this.activatedRoute.snapshot.queryParamMap.get(EMAIL_VERIFICATION_CONSTANTS.TOKEN_PARAM_NAME);
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return lastValueFrom(this.account.get())
            .then((account: UserDTO) => {
                try {
                    if (account) {
                        const isInitialAccountRequest = !this.authenticated;

                        this.userIdentity = account;
                        this.authenticated = true;
                        this.userActivityService.startTrackUserActivity();
                        this.checkAuthorizationTimer();

                        if (isInitialAccountRequest) {
                            /*
                             * we need to check if user email is verified first.
                             * after successful verification, check whether the EULA dialog should be shown.
                             */
                            this.checkIfEmailVerificationOrEulaDialogShouldBeShown();
                        }
                    } else {
                        this.userIdentity = null;
                        this.authenticated = false;
                        this.userActivityService.stopTrackUserActivity();
                        this.stopWorkerTimers();
                        this.clearTimer();
                    }
                    this.authenticationState.next(this.userIdentity);
                    return this.userIdentity;
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((_err) => {
                this.userIdentity = null;
                this.authenticated = false;
                this.authenticationState.next(this.userIdentity);
                this.userActivityService.stopTrackUserActivity();
                this.stopWorkerTimers();
                this.clearTimer();
                return null;
            });
    }

    isAuthenticated(): boolean {
        return this.authenticated;
    }

    getAuthenticationState(): Observable<UserDTO> {
        return this.authenticationState.asObservable();
    }

    checkAuthorizationTimer() {
        if (this.authorizationTimer) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.authorizationTimer = setInterval(() => {
                this.handleAuthorizationCheck();
            }, this.timeInterval);
        });
    }

    private handleAuthorizationCheck(): void {
        this.ngZone.run(() => {
            if (!this.isAuthenticated()) {
                this.clearTimer();
                return;
            }

            firstValueFrom(this.account.get()).catch((err) => {
                if (err.status === HttpStatusCode.Unauthorized) {
                    this.identity(true);
                }
            });
        });
    }

    stopWorkerTimers() {
        this.autoLogoutWorkerService.postMessage({ type: MessageType.STOP_TIMERS });
    }

    clearTimer() {
        clearInterval(this.authorizationTimer);
        this.authorizationTimer = undefined;
    }

    currentUserInTheSameDomain(userLogin: string): boolean {
        if (!this.userIdentity) {
            return false;
        }

        return this.getDomainFromEmail(this.userIdentity.login) === this.getDomainFromEmail(userLogin);
    }

    public getStoredEmailVerificationToken(): string {
        return this.token ?? '';
    }

    public getLoginOrUndefined(): string {
        return this.userIdentity?.login;
    }

    private logoutRequest(isAutoLogout: boolean): void {
        this.userActivityService.stopTrackUserActivity();
        this.http.post('api/logout', {}).subscribe(() => {
            this.identity(true);
            if (!isAutoLogout) {
                this.autoLogoutWorkerService.postMessage({ type: MessageType.LOGOUT });
            }
        });
    }

    private getDomainFromEmail(email: string): string {
        if (!email || email.indexOf('@') < 0) {
            return '';
        }

        return email.substring(email.indexOf('@') + 1).trim();
    }

    private checkIfEmailVerificationOrEulaDialogShouldBeShown(): void {
        if (this.userIdentity.emailVerificationRequired) {
            this.showEmailVerificationDialog();
            return;
        }

        this.showEulaDialogIfRequired();
    }

    private showEmailVerificationDialog(): void {
        this.dialogService
            .open(EmailVerificationDialogComponent, EmailVerificationDialogComponent.DIALOG_CONFIG)
            .afterClosed()
            .pipe(first())
            .subscribe(() => {
                this.showEulaDialogIfRequired();
            });
    }

    private showEulaDialogIfRequired(): void {
        if (this.userIdentity.eulaAcceptanceRequired) {
            this.dialogService.open(AcceptEulaDialogComponent, AcceptEulaDialogComponent.DIALOG_CONFIG);
        }
    }
}
